import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FaCheckCircle, FaCog, FaChartLine, FaImages, FaCreditCard } from 'react-icons/fa'
import { openExternalUrl } from '@renderer/lib/runtime'

interface CongratulationsModalProps {
  isOpen: boolean
  onClose: () => void
}

export const CongratulationsModal: React.FC<CongratulationsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()

  const handleClose = async (): Promise<void> => {
    try {
      // Update onboarding to completed and mark modal as shown when user closes modal
      const response = await fetch(`${process.env.VITE_API_URL}/api/users/update-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          onboardingCompleted: true,
          onboardingStep: 'completed',
          onboardingModalShown: true
        }),
        credentials: 'include'
      })

      if (response.ok) {
        console.log(
          '[CONGRATULATIONS] Successfully marked onboarding as completed and modal as shown'
        )
      } else {
        console.error('Failed to update onboarding')
      }
    } catch (error) {
      console.error('Error updating onboarding:', error)
    }
    // Always close the modal even if API call fails
    onClose()
  }

  const handleGetStarted = async (): Promise<void> => {
    try {
      // Update onboarding to completed and mark modal as shown
      const response = await fetch(`${process.env.VITE_API_URL}/api/users/update-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          onboardingCompleted: true,
          onboardingStep: 'completed',
          onboardingModalShown: true
        }),
        credentials: 'include'
      })

      if (response.ok) {
        console.log('[CONGRATULATIONS] Successfully marked onboarding modal as shown')
        onClose()
      } else {
        console.error('Failed to update onboarding')
        onClose() // Close anyway
      }
    } catch (error) {
      console.error('Error updating onboarding:', error)
      onClose() // Close anyway
    }
  }

  const handleNavigate = (href: string): void => {
    // Open dashboard links in external browser (Tauri opener, Electron setWindowOpenHandler, or window.open)
    const fullUrl = `${process.env.VITE_API_URL}${href}`
    void openExternalUrl(fullUrl)
    onClose()
  }

  if (!isOpen) return null

  const nextSteps = [
    {
      icon: FaCog,
      text: t('onboarding.congratulations.customize', 'Customize Your Project'),
      href: '/dashboard/projects'
    },
    {
      icon: FaChartLine,
      text: t('onboarding.congratulations.analytics', 'View Analytics'),
      href: '/dashboard'
    },
    {
      icon: FaImages,
      text: t('onboarding.congratulations.gallery', 'Browse Gallery'),
      href: '/dashboard/gallery'
    },
    {
      icon: FaCreditCard,
      text: t('onboarding.congratulations.payment', 'Manage Subscription'),
      href: '/dashboard/subscription'
    }
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 z-10"
          >
            {/* Close button */}
            <button
              title="Close"
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Content */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="inline-block mb-4"
              >
                <FaCheckCircle className="w-20 h-20 text-green-500" />
              </motion.div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t('onboarding.congratulations.title', 'Congratulations!')}
              </h2>
              <p className="text-lg text-gray-600">
                {t(
                  'onboarding.congratulations.subtitle',
                  "You've successfully completed your first photobooth session!"
                )}
              </p>
            </div>

            {/* Next Steps */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                {t('onboarding.congratulations.nextSteps', 'What would you like to do next?')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {nextSteps.map((step, index) => {
                  const Icon = step.icon
                  return (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      onClick={() => handleNavigate(step.href)}
                      className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
                    >
                      <Icon className="w-6 h-6 text-blueSparkle flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{step.text}</span>
                    </motion.button>
                  )
                })}
              </div>
            </div>

            {/* Get Started Button */}
            <div className="flex justify-center">
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                onClick={handleGetStarted}
                className="px-8 py-3 bg-blueSparkle text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                {t('onboarding.congratulations.getStarted', 'Get Started')}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
